import React, { FC, useEffect } from 'react';
import PositionTable from './table/table.component';
import ExposureGraphics from './exposureGraphics/exposureGraphics.component';
import CreditRatingsTable from './creditRatings/creditRatings.component';
import AggregateFixedIncomeTable from './aggTable/aggTable.component';
import useTitleBarSelect from '../../../../../hooks/useTitleBarSelect';
import { useDispatch } from 'react-redux';
import { clearPdfExports } from '../../../../../redux/fileExport/actions';
import { GeneralOverviewPageRenderedComponentProps } from '../../../../layout/general/SimpleGeneralPageWrapper';
import { Select } from '../../../../../types/redux/pages/PagesStore';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../../types/redux/pages/pageTypes';
import { useMancoFixedIncomePageModel } from '../../../../../api/rs-hg/manco';
import { QueryBoundary } from '../../../../utilityComponents/QueryBoundary';
import { BondDataServer } from './table/table.data';

export interface FixedIncomeComponent {
  bondData: BondDataServer;
  isFetching: boolean;
}

export type FixedIncomePages =
  | 'positionTable'
  | 'exposureGraphics'
  | 'creditRatingsTable';

export enum FixedIncomeOption {
  PC_OF_NAV = 'Percentage of NAV',
  BASE_CURRENCY = 'Base Currency',
}

const handleComponent = (selectedPage: string | null) => {
  switch (selectedPage) {
    case 'positionTable':
      return PositionTable;
    case 'exposureGraphics':
      return ExposureGraphics;
    case 'creditRatingsTable':
      return CreditRatingsTable;
    default:
      return PositionTable;
  }
};

const MancoFixedIncome: FC<GeneralOverviewPageRenderedComponentProps> = ({
  positionDate,
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Visualisation: ',
        titleBarKey: 'selectVisualisation',
        values: [
          {
            label: 'Position Table',
            value: 'positionTable',
          },
          {
            label: 'Exposure Graphics',
            value: 'exposureGraphics',
          },
          {
            label: 'Credit Ratings Table',
            value: 'creditRatingsTable',
          },
        ],
        displayOnOverviewPage: true,
        aValueIsAlwaysSelected: true,
        currentValue: 'positionTable',
      },
    ];

    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent(
          'selectVisualisation',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, [dispatch]);

  const selectedPage = useTitleBarSelect('selectVisualisation');
  const SelectedComponent = handleComponent(selectedPage);

  useEffect(() => {
    dispatch(clearPdfExports());
  }, [selectedPage, dispatch]);

  const fixedIncomeQuery = useMancoFixedIncomePageModel(positionDate);
  const bondData = (fixedIncomeQuery.data?.data ??
    null) as BondDataServer | null;

  return (
    <QueryBoundary<BondDataServer>
      isLoading={fixedIncomeQuery.isLoading}
      isFetching={fixedIncomeQuery.isFetching}
      isError={fixedIncomeQuery.isError}
      error={fixedIncomeQuery.error}
      data={bondData}
      loadingMessages={[
        'Loading Manco Fixed Income Data...',
        'This may take a few moments...',
      ]}
      errorMessage="No data found"
      noDataMessage="No data found"
    >
      {(report) => (
        <>
          <AggregateFixedIncomeTable
            currentPage={(selectedPage as FixedIncomePages) || 'positionTable'}
            bondData={report}
            isFetching={fixedIncomeQuery.isFetching}
          />
          <SelectedComponent
            bondData={report}
            isFetching={fixedIncomeQuery.isFetching}
          />
        </>
      )}
    </QueryBoundary>
  );
};

export default MancoFixedIncome;
